import { useRef, useState, useCallback, useMemo } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

interface UseReCaptchaOptions {
  action?: string;
}

const reCaptchaV2Sitekey = process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY || '';
const reCaptchaV3Sitekey = process.env.NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY || '';

if (!reCaptchaV3Sitekey) {
  console.error('NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY is not defined!');
}

if (!reCaptchaV2Sitekey) {
  console.error('NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY is not defined!');
}

export const useReCaptcha = ({ action = 'register' }: UseReCaptchaOptions = {}) => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const recaptchaV2Ref = useRef<ReCAPTCHA>(null);

  const [isLoading, setIsLoading] = useState(false);

  const getV2Token = useCallback(async (): Promise<string | null> => {
    if (!recaptchaV2Ref.current) return null;

    setIsLoading(true);

    try {
      const token = await recaptchaV2Ref.current.executeAsync();
      return token || null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const execute = useCallback(async () => {
    let v3Token = '';

    if (executeRecaptcha) {
      try {
        v3Token = await executeRecaptcha(action);
      } catch (e) {
        console.error('reCAPTCHA v3 failed', e);
      }
    }

    if (v3Token) {
      return { v3Token };
    }

    const v2Token = await getV2Token();

    if (!v2Token) {
      throw new Error('RECAPTCHA_TOKEN_FAILED');
    }

    return { v3Token, v2Token };
  }, [executeRecaptcha, action, getV2Token]);

  const handleV2Error = () => {
    console.error(
      'reCAPTCHA v2 script failed to load or encountered an error. Please disable adblockers or try another network.',
    );
  };

  const RecaptchaV2 = useMemo(() => {
    return reCaptchaV2Sitekey ? (
      <ReCAPTCHA ref={recaptchaV2Ref} sitekey={reCaptchaV2Sitekey} size="invisible" onErrored={handleV2Error} />
    ) : null;
  }, []);

  return {
    execute,
    RecaptchaV2Component: RecaptchaV2,
    isLoading,
  };
};
